import { IconCloudUpload, IconExternalLink, IconQuestionMark } from '@tabler/icons-react';
import { useState } from 'react';
import { Anchor, Button, Checkbox, Group, Modal, Stack, Text, Tooltip } from '@mantine/core';
import { useParams } from 'react-router';
import useFetch from '../../helpers/useFetch';
import ErrorMessage from '../ErrorMessage';
import FileUpload from '../FileUpload';

const ProfileImportModal = ({ isOpen, done, close }) => {
  const fetch = useFetch();

  const [uploading, setUploading] = useState(false);
  const [skipExistingProfiles, setSkipExistingProfiles] = useState(true);
  const [mergeTags, setMergeTags] = useState(true);
  const [overwriteRegAndSubDates, setOverwriteRegAndSubDates] = useState(true);
  const [error, setError] = useState();
  const [file, setFile] = useState();
  const { accountId, domainId } = useParams();

  const upload = () => {
    if (!file) {
      return;
    }
    setUploading(true);
    let fd = new FormData();
    fd.append('file', file);
    fd.append('skipExistingProfiles', skipExistingProfiles);
    fd.append('mergeTags', mergeTags);
    fd.append('overwriteRegAndSubDates', overwriteRegAndSubDates);
    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/imports`, {
      method: 'POST',
      headers: {},
      body: fd,
    })
      .then(() => {
        setUploading(false);
        setFile(undefined);
        done();
      })
      .catch(setError);
  };

  if (error) {
    return <ErrorMessage message={error.message} />;
  }

  return (
    <Modal opened={isOpen} onClose={() => close()} title="Profile Import">
      <Stack gap="md">
        <FileUpload onFileSet={(files) => setFile(files[0])} />

        <Checkbox
          name="skipExistingProfiles"
          label="Skip Existing Profiles"
          onChange={(e) => setSkipExistingProfiles(e.currentTarget.checked)}
          checked={skipExistingProfiles}
        />

        <Checkbox
          name="doNotOverwriteDates"
          disabled={skipExistingProfiles}
          label={
            <>
              Overwrite 'Registered At' and 'Subscribed At' fields{' '}
              <Tooltip
                label="If checked, for any profile which already exists in our database, the values of 'Registered At' and 'Subscribed At' will be overwritted by the ones in the import file. If unchecked, the field values will not be changed."
                position="right"
                multiline
                w={300}
              >
                <IconQuestionMark size={14} style={{ display: 'inline', cursor: 'help' }} />
              </Tooltip>
            </>
          }
          onChange={(e) => setOverwriteRegAndSubDates(e.currentTarget.checked)}
          checked={overwriteRegAndSubDates}
        />

        <Checkbox
          name="mergeTags"
          disabled={skipExistingProfiles}
          label={
            <>
              Merge Tags for Existing Profiles{' '}
              <Tooltip
                label="If checked, for any profile which already exists in our database, the tags provided will be added to the tags in our database. If unchecked, tags will be overwritten."
                position="right"
                multiline
                w={300}
              >
                <IconQuestionMark size={14} style={{ display: 'inline', cursor: 'help' }} />
              </Tooltip>
            </>
          }
          onChange={(e) => setMergeTags(e.currentTarget.checked)}
          checked={mergeTags}
        />

        <Text size="xs" c="dimmed" data-tour="profileImportCsvFormat">
          You may{' '}
          <Anchor href="https://releva.ai/docs/downloads/releva-profile-import-template.csv" target="_blank">
            download a sample CSV file <IconExternalLink size={12} style={{ display: 'inline' }} />
          </Anchor>{' '}
          or{' '}
          <Anchor href="https://releva.ai/documentation/data-platform/importing-data/" target="_blank">
            check out the supported file format here <IconExternalLink size={12} style={{ display: 'inline' }} />
          </Anchor>
          .
        </Text>
      </Stack>

      <Group justify="flex-end" mt="md">
        <Button variant="default" onClick={() => close()}>
          Close
        </Button>
        <Button
          disabled={!file || uploading}
          onClick={upload}
          data-tour="importProfilesModalBtn"
          rightSection={<IconCloudUpload size={16} />}
        >
          Import
        </Button>
      </Group>
    </Modal>
  );
};

export default ProfileImportModal;
